// Centralized Europe/Paris display primitives (Phase 91).
//
// The webapp has no date lib (no date-fns/dayjs/luxon) — everything is native
// `Date` + `Intl`. The bug this fixes: ~60% of call-sites call `toLocale*` /
// `Intl.DateTimeFormat` WITHOUT `timeZone`, so a visitor outside France sees
// slot hours shifted to their browser's zone. These three helpers FORCE
// Europe/Paris internally so no call-site can re-leak the browser zone.
//
// DST-correct by construction: `Intl` + `timeZone: 'Europe/Paris'` resolves the
// +1 (CET) / +2 (CEST) offset automatically — never do offset arithmetic.
// Refs: phase 91. Server-side twin: supabase/functions/_shared/gcal.ts:225.

const PARIS = 'Europe/Paris'

/**
 * Balise BCP-47 par langue servie (Phase 95). L'anglais du projet est
 * `en-GB`, jamais la variante américaine : en-GB rend « Sunday 16 August »
 * (jour avant le mois, comme le français) là où l'américaine rendrait
 * « Sunday, August 16 ». Le studio est européen et l'inversion jour/mois est
 * une source réelle d'erreur de réservation. Constante partagée pour
 * qu'aucune page n'ait à la retaper.
 * Refs: phase 95.
 */
export const INTL_LOCALE = { fr: 'fr-FR', en: 'en-GB' } as const

/**
 * Render any timestamptz in Europe/Paris wall-clock, regardless of process TZ.
 * Caller-supplied `opts` are spread FIRST, then `timeZone` is forced LAST so a
 * caller can never override the studio zone. Callers own null-guarding the
 * input (the primitive does not swallow invalid dates).
 *
 * Phase 95 — `locale` n'ouvre que le FORMAT (D-08). Le fuseau reste écrasé en
 * dernier : ni la locale ni une option d'appelant ne peuvent le déplacer. Une
 * séance de 14 h à Paris se lit 14 h en anglais. Le paramètre a un défaut, donc
 * aucun des appelants existants ne bouge.
 */
export function formatParis(
  input: string | Date,
  opts: Intl.DateTimeFormatOptions,
  locale: string = 'fr-FR',
): string {
  // Garde d'EXECUTION, et le type reste volontairement non-nullable : les
  // appelants TypeScript doivent continuer a prouver qu'ils ont une date. Mais
  // au passage du reseau le type MENT — c'est exactement ce qui a produit le
  // « 1er janvier 1970 » d'AdminAvis le 2026-09-17, ou une colonne declaree
  // `string` etait NULL sur 27 lignes sur 29.
  //
  // `new Date(null)` ne vaut PAS `NaN` : il vaut ZERO EPOQUE, et
  // `Intl.DateTimeFormat` formate 1970 sans broncher. Aucune erreur, aucune
  // trace : juste une fausse date affichee a un humain. (Gemini Gate B.)
  if (!input) return ''
  return new Intl.DateTimeFormat(locale, { ...opts, timeZone: PARIS }).format(
    new Date(input),
  )
}

/**
 * Paris wall-clock hour as a decimal for grid positioning (14:30 Paris -> 14.5).
 * Replaces raw browser-local hour+minute math at call-sites (which reads the
 * visitor's zone). Mirrors the gcal.ts `toParisIsoWallClock` formatToParts pattern.
 *
 * Ne prend PAS de locale, et ne doit jamais en prendre (Phase 95) : son `en-GB`
 * n'est pas de l'affichage : c'est ce qui garantit un `hour12: false` stable pour
 * du *parsing*. Une locale variable rendrait « 2 PM » et l'arithmétique
 * ci-dessous produirait un nombre faux.
 */
export function parisHourDecimal(input: string | Date): number {
  const parts = new Intl.DateTimeFormat('en-GB', {
    timeZone: PARIS,
    hour: '2-digit',
    minute: '2-digit',
    hour12: false,
  }).formatToParts(new Date(input))
  const get = (type: string) => parts.find((p) => p.type === type)?.value ?? '0'
  // formatToParts values are STRINGS — Number() before arithmetic or you get
  // string concatenation. Some runtimes emit hour '24' at midnight -> 0.
  let hour = Number(get('hour'))
  if (hour === 24) hour = 0
  return hour + Number(get('minute')) / 60
}

/**
 * yyyy-mm-dd Paris calendar day (handles the midnight boundary). Byte-matches
 * the existing SlotPicker.localDateStr (same 'fr-CA' + Europe/Paris) by
 * construction — the primitive for NEW day-level code in 91-02.
 *
 * Ne prend PAS de locale, et ne doit jamais en prendre (Phase 95) : son 'fr-CA'
 * produit `yyyy-mm-dd`, un format de CLÉ, pas un affichage. Le rendre variable
 * casserait silencieusement toutes les comparaisons de jour (en-GB rendrait
 * `16/08/2026`, qui ne se compare ni ne se trie).
 */
export function parisDateKey(input: string | Date): string {
  return new Date(input).toLocaleDateString('fr-CA', { timeZone: PARIS })
}
